import { useState } from 'react'
import { Box, Button, IconButton, TextField, Typography } from '@mui/material'
import {
    Instagram, Youtube, Twitter, Twitch, Linkedin, Facebook, Github, Globe, Music2, X,
} from 'lucide-react'

import { addSocialLink } from '../../../services/api/SocialLinkDataService'
import { SocialLink } from '../../../model/SocialLink'
import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

// Curated popular platforms mapped to the backend Icon codes (utils/socialIcons ICON_NAMES order).
const PLATFORMS = [
    { code: 1, name: 'Instagram', Icon: Instagram },
    { code: 4, name: 'TikTok', Icon: Music2 },
    { code: 10, name: 'YouTube', Icon: Youtube },
    { code: 3, name: 'Twitter / X', Icon: Twitter },
    { code: 11, name: 'Twitch', Icon: Twitch },
    { code: 18, name: 'LinkedIn', Icon: Linkedin },
    { code: 13, name: 'Spotify', Icon: Music2 },
    { code: 2, name: 'Facebook', Icon: Facebook },
    { code: 17, name: 'GitHub', Icon: Github },
    { code: 21, name: 'Website', Icon: Globe },
]

export default function SocialsStep({ pageId, goNext }) {
    const [selected, setSelected] = useState([]) // ordered list of platform codes
    const [values, setValues] = useState({}) // code -> raw handle/url
    const [saving, setSaving] = useState(false)

    const platformByCode = (code) => PLATFORMS.find((p) => p.code === code)

    const toggle = (code) => {
        setSelected((prev) => (prev.includes(code) ? prev : [...prev, code]))
    }

    const remove = (code) => {
        setSelected((prev) => prev.filter((c) => c !== code))
        setValues((prev) => {
            const next = { ...prev }
            delete next[code]
            return next
        })
    }

    const handleContinue = async () => {
        if (!pageId) return
        setSaving(true)
        const toSave = selected.filter((code) => (values[code] || '').trim().length > 0)
        // Pass the raw value; SocialLinkDataService.addSocialLink normalizes the URL and resolves the
        // icon name from the code before POSTing to /socials/{pageId}.
        await Promise.all(
            toSave.map((code) => addSocialLink(new SocialLink('', values[code].trim(), code, pageId)))
        )
        goNext()
    }

    return (
        <Box>
            <Typography sx={s.title}>What platforms are you on?</Typography>
            <Typography sx={s.subtitle}>Let's add them right away.</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {PLATFORMS.map(({ code, name, Icon }) => {
                    const isSelected = selected.includes(code)
                    return (
                        <Button
                            key={code}
                            onClick={() => toggle(code)}
                            startIcon={<Icon size={16} />}
                            disableElevation
                            sx={{
                                textTransform: 'none',
                                fontFamily: 'Catamaran',
                                fontWeight: 600,
                                borderRadius: '999px',
                                px: 1.6,
                                py: 0.6,
                                border: isSelected ? `2px solid ${ACCENT}` : '2px solid #e0e0e0',
                                backgroundColor: isSelected ? 'rgba(255,20,147,0.08)' : '#fff',
                                color: isSelected ? ACCENT : 'text.gray',
                                '&:hover': { backgroundColor: isSelected ? 'rgba(255,20,147,0.12)' : '#f7f7f7' },
                            }}
                        >
                            {name}
                        </Button>
                    )
                })}
            </Box>

            {selected.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
                    {selected.map((code) => {
                        const p = platformByCode(code)
                        return (
                            <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={p?.name}
                                    placeholder={`Paste your ${p?.name} link or @handle`}
                                    value={values[code] || ''}
                                    onChange={(e) => setValues((prev) => ({ ...prev, [code]: e.target.value }))}
                                />
                                <IconButton aria-label="remove" onClick={() => remove(code)} size="small">
                                    <X size={18} />
                                </IconButton>
                            </Box>
                        )
                    })}
                </Box>
            )}

            <Box sx={s.footer}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleContinue}
                    disabled={saving}
                    sx={s.ctaButton}
                >
                    {saving ? 'Saving…' : 'Continue'}
                </Button>
                <Button onClick={goNext} disabled={saving} sx={s.skipButton}>
                    Skip for now
                </Button>
            </Box>
        </Box>
    )
}
